import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AddIconComponent } from '@kuiper/icons';
import {
  OptionItem,
  SelectComponent,
  UserDropdownViewComponent,
  ValidationMessageComponent,
} from 'all-libraries/ibusiness-core/src/ui-components';
import { LookupService } from 'all-libraries/ibusiness-core/src/core/common/services/lookup/lookup.service';
import { Status } from 'all-libraries/ibusiness-core/src/core/common/enums/user-status';
import { Option } from 'all-libraries/ibusiness-core/src/core/common/models/option.model';
import { Meta } from 'all-libraries/ibusiness-core/src/core/common/models/api-response.model';
import {
  STEP_ACTION_TYPE_OPTIONS,
  STEP_ACTION_WHEN_OPTIONS,
  StepActionFormGroup,
} from './workflow-configuration-step-action.const';

const selectedUsersByFormGroup = new WeakMap<StepActionFormGroup, Option[]>();

@Component({
  selector: 'lib-workflow-configuration-step-action',
  imports: [
    ReactiveFormsModule,
    SelectComponent,
    TranslocoPipe,
    UserDropdownViewComponent,
    AddIconComponent,
    NgTemplateOutlet,
    ValidationMessageComponent,
  ],
  templateUrl: './workflow-configuration-step-action.component.html',
  styleUrl: './workflow-configuration-step-action.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowConfigurationStepActionComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly lookupService = inject(LookupService);
  private readonly destroyRef = inject(DestroyRef);

  formGroup = input.required<StepActionFormGroup>();
  actionIndex = input(0);
  initialSelectedUsers = input<OptionItem[]>([]);

  readonly whenOptions = computed(() =>
    STEP_ACTION_WHEN_OPTIONS.map(option => ({
      ...option,
      label: this.transloco.translate(option.label),
    }))
  );
  readonly actionOptions = computed(() =>
    STEP_ACTION_TYPE_OPTIONS.map(option => ({
      ...option,
      label: this.transloco.translate(option.label),
    }))
  );

  readonly selectedUsers = linkedSignal<Option[]>(() => {
    const group = this.formGroup();
    return untracked(() => this.getUsersForFormGroup(group));
  });
  readonly userCount = computed(() => this.selectedUsers().length);
  readonly showCustomToggleButton = computed(() => this.userCount() === 0);
  readonly canRemoveUsers = computed(
    () => this.userCount() > 0 && !this.isAddMode()
  );

  readonly isAddMode = linkedSignal(() => {
    const group = this.formGroup();
    return untracked(() => this.getUsersForFormGroup(group).length === 0);
  });
  readonly userDropdownList = linkedSignal<Option[]>(() => {
    this.formGroup();
    return [];
  });
  readonly userDropdownMeta = linkedSignal<Meta | null>(() => {
    this.formGroup();
    return null;
  });
  readonly isLoadingDropdown = signal(false);

  handleMenuToggle(event: { currentPage: number; search: string }): void {
    this.isLoadingDropdown.set(true);

    if (event.currentPage === 1) {
      this.userDropdownList.set([]);
    }

    if (this.isAddMode()) {
      this.getUsers(event);
      return;
    }

    this.isLoadingDropdown.set(false);
  }

  getUsers({
    currentPage,
    search,
  }: {
    currentPage: number;
    search: string;
  }): void {
    this.isLoadingDropdown.set(true);

    this.lookupService
      .userLookup({
        name: search,
        page: currentPage,
        filtersNotState: Status.INACTIVE,
        filtersNotStateCancelled: Status.CANCELLED,
      })
      .pipe(
        finalize(() => this.isLoadingDropdown.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: res => {
          const selectedKeys = new Set(
            this.selectedUsers().map(user => this.getUserKey(user))
          );
          const users = (res.data as Option[]).filter(
            user => !selectedKeys.has(this.getUserKey(user))
          );

          this.userDropdownList.update(current =>
            currentPage === 1 ? users : [...current, ...users]
          );
          this.userDropdownMeta.set({
            ...res.meta,
            total: search
              ? res.meta.total
              : Math.max(res.meta.total - selectedKeys.size, 0),
          });
        },
      });
  }

  addUser(user: Option): void {
    const userId = this.toUserId(user);
    if (userId == null) {
      return;
    }

    const userKey = this.getUserKey(user);
    const alreadySelected = this.selectedUsers().some(
      selected => this.getUserKey(selected) === userKey
    );
    if (alreadySelected) {
      return;
    }

    this.selectedUsers.update(current => [
      ...current,
      this.toDropdownUser(user),
    ]);
    this.userDropdownList.update(current =>
      current.filter(item => this.getUserKey(item) !== userKey)
    );
    this.isAddMode.set(true);
    this.cacheSelectedUsers();
    this.syncUserIds();
  }

  removeUser(userId: number | string): void {
    const userKey = String(userId);

    this.selectedUsers.update(current =>
      current.filter(user => this.getUserKey(user) !== userKey)
    );

    if (!this.isAddMode()) {
      this.userDropdownList.update(current =>
        current.filter(user => this.getUserKey(user) !== userKey)
      );
    }

    if (!this.userCount()) {
      this.isAddMode.set(true);
    }

    this.cacheSelectedUsers();
    this.syncUserIds();
  }

  switchToAddMode(): void {
    this.isAddMode.set(true);
    this.handleMenuToggle({ currentPage: 1, search: '' });
  }

  resetDropdown(isOpen = false): void {
    if (isOpen) {
      return;
    }

    this.isAddMode.set(this.userCount() === 0);
    this.userDropdownList.set([]);
    this.userDropdownMeta.set(null);
  }

  private syncUserIds(): void {
    const userIds = this.selectedUsers()
      .map(user => this.toUserId(user))
      .filter((userId): userId is number => userId != null);

    this.formGroup().controls.user_ids.setValue(userIds);
    this.formGroup().controls.user_ids.markAsDirty();
    this.formGroup().controls.user_ids.markAsTouched();
  }

  private cacheSelectedUsers(): void {
    selectedUsersByFormGroup.set(this.formGroup(), this.selectedUsers());
  }

  private getUsersForFormGroup(group: StepActionFormGroup): Option[] {
    return (
      selectedUsersByFormGroup.get(group) ??
      this.initialSelectedUsers().map(user => this.toDropdownUser(user))
    );
  }

  private toDropdownUser(user: Option | OptionItem): Option {
    const value = String(user.value ?? user.id ?? '');

    return {
      id: value,
      value,
      label: user.label ?? user.name ?? '',
      name: user.name ?? user.label ?? '',
      image: user.image,
      job_title: 'job_title' in user ? user.job_title : undefined,
    };
  }

  private getUserKey(user: Option | OptionItem): string {
    return String(user.value ?? user.id ?? '');
  }

  private toUserId(user: Option | OptionItem | null): number | null {
    const rawValue = user?.value ?? user?.id;

    if (rawValue == null || rawValue === '') {
      return null;
    }

    const userId = Number(rawValue);
    return Number.isFinite(userId) ? userId : null;
  }
}
